import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Form, Row, Col } from 'antd';

const { TextArea } = Input;
const { Option } = Select;

// 节点类型选项
const nodeTypeOptions = [
  { value: 'rect', label: '对象', color: '#70E483' },
  { value: 'circle', label: '属性', color: '#52c41a' },
  { value: 'diamond', label: '关系', color: '#fa8c16' },
  { value: 'polygon', label: '过程', color: '#722ed1' },
  { value: 'dynamic-group', label: '分组', color: '#13c2c2' },
  { value: 'state', label: '状态', color: '#1890ff' },
];

interface NodeEditDialogProps {
  visible: boolean;
  nodeData?: any;
  onConfirm: (nodeData: any) => void;
  onCancel: () => void;
}

const NodeEditDialog: React.FC<NodeEditDialogProps> = ({
  visible,
  nodeData,
  onConfirm,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // 获取节点的文本值（处理对象和字符串情况）
  const getNodeText = (nodeData: any): string => {
    if (!nodeData) return '';

    // 如果 text 是字符串，直接返回
    if (typeof nodeData.text === 'string') {
      return nodeData.text;
    }

    // 如果 text 是对象，尝试获取 value 属性
    if (
      nodeData.text &&
      typeof nodeData.text === 'object' &&
      nodeData.text.value
    ) {
      return nodeData.text.value;
    }

    // 尝试从 properties.name 获取
    if (
      nodeData.properties?.name &&
      typeof nodeData.properties.name === 'string'
    ) {
      return nodeData.properties.name;
    }

    // 最后返回 id 或空字符串
    return nodeData.id || '';
  };

  // 监听弹窗显示状态，设置表单初始值
  useEffect(() => {
    if (visible && nodeData) {
      console.log('设置节点编辑表单数据:', nodeData);

      // 获取节点文本
      const nodeText = getNodeText(nodeData);

      // 设置表单字段值（只设置名称和描述）
      form.setFieldsValue({
        name: nodeText,
        description: nodeData.properties?.description || '',
      });
    } else if (visible && !nodeData) {
      // 新建节点时重置表单
      form.resetFields();
    }
  }, [visible, nodeData, form]);

  // 确认编辑
  const handleConfirm = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      console.log('节点编辑表单值:', values);

      // 构建更新后的节点数据，只包含名称和描述
      const updatedNodeData = {
        ...nodeData,
        text: values.name,
        properties: {
          ...nodeData?.properties,
          name: values.name,
          description: values.description,
        },
      };

      onConfirm(updatedNodeData);
      form.resetFields();
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 取消编辑
  const handleCancel = () => {
    onCancel();
    form.resetFields();
  };

  // 获取节点类型的颜色
  const getNodeTypeColor = (type: string) => {
    const option = nodeTypeOptions.find(opt => opt.value === type);
    return option?.color || '#666';
  };

  // 获取节点类型的显示标签
  const getNodeTypeLabel = (type: string, properties?: any) => {
    // 特殊处理实体分组
    if (type === 'dynamic-group' && properties?.isEntityGroup) {
      return '实体分组';
    }

    // 特殊处理状态节点
    if (type === 'state' || properties?.isState) {
      return '状态';
    }

    // 默认查找
    const option = nodeTypeOptions.find(opt => opt.value === type);
    return option?.label || type;
  };

  return (
    <Modal
      title={nodeData ? '编辑节点' : '新建节点'}
      open={visible}
      onOk={handleConfirm}
      onCancel={handleCancel}
      width={500}
      okText="确认"
      cancelText="取消"
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        {/* 节点名称 */}
        <Form.Item
          label="节点名称"
          name="name"
          rules={[
            { required: true, message: '请输入节点名称' },
            { max: 200, message: '节点名称不能超过200个字符' },
          ]}
        >
          <TextArea
            rows={3}
            placeholder="请输入节点名称（支持多行）"
            maxLength={200}
            showCount
          />
        </Form.Item>

        {/* 节点类型 - 只读显示 */}
        <Form.Item label="节点类型">
          <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getNodeTypeColor(nodeData?.type) }}
            />
            <span>
              {getNodeTypeLabel(nodeData?.type, nodeData?.properties)}
            </span>
            {/* <span className="text-gray-500 text-sm">(只读)</span> */}
          </div>
        </Form.Item>

        {/* 节点描述 */}
        <Form.Item label="节点描述" name="description">
          <TextArea
            rows={3}
            placeholder="请输入节点描述（可选）"
            maxLength={200}
            showCount
          />
        </Form.Item>

        {/* 节点信息预览 */}
        {nodeData && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-2">节点信息：</div>
            <div className="space-y-1 text-xs">
              <div>ID: {nodeData.id}</div>
              <div>
                当前类型:
                <span
                  className="ml-1 px-2 py-1 rounded text-white"
                  style={{ backgroundColor: getNodeTypeColor(nodeData.type) }}
                >
                  {getNodeTypeLabel(nodeData.type, nodeData.properties)}
                </span>
              </div>
              <div>
                当前位置: ({nodeData.x}, {nodeData.y})
              </div>
              {/* 显示分组信息 */}
              {nodeData.properties?.isGroup && (
                <div>
                  分组模式:{' '}
                  {nodeData.properties?.isEntityGroup ? '实体分组' : '普通分组'}
                </div>
              )}
              {/* 显示状态信息 */}
              {nodeData.properties?.isState && (
                <div>父分组: {nodeData.properties?.parentGroupId || '无'}</div>
              )}
              {/* 显示分组中的状态数量 */}
              {nodeData.properties?.children &&
                nodeData.properties.children.length > 0 && (
                  <div>子节点数量: {nodeData.properties.children.length}</div>
                )}
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default NodeEditDialog;
